import React, { useEffect, useRef, useState } from 'react'

interface LazyLoopingVideoProps {
    /** Muted, looping video source (mp4). */
    src: string
    /** Extra styles merged onto the <video> (positioning, sizing, masks, etc.). */
    style?: React.CSSProperties
    className?: string
    objectFit?: React.CSSProperties['objectFit']
    objectPosition?: string
    /** setTimeout fallback (ms) when requestIdleCallback is unavailable. */
    idleTimeout?: number
    /** Fired once the video can play (i.e. has begun cross-fading in). */
    onReady?: () => void
}

/**
 * Muted, looping video that is only mounted AFTER first paint (via
 * requestIdleCallback) so downloading/decoding it never blocks the initial render or
 * LCP. It starts at opacity 0 and cross-fades in once it can play, so whatever sits
 * behind it (a poster/fallback image) shows instantly. Muted + inline so it autoplays.
 * Ported from the flights-pwa cabin/upgrade media pattern.
 */
const LazyLoopingVideo: React.FC<LazyLoopingVideoProps> = ({
    src,
    style,
    className,
    objectFit = 'cover',
    objectPosition = 'center',
    idleTimeout = 300,
    onReady,
}) => {
    // Gate mounting until the browser is idle so the video work stays off the critical path.
    const [shouldMount, setShouldMount] = useState(false)
    const [canPlay, setCanPlay] = useState(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)

    useEffect(() => {
        const win = window as Window & {
            requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
            cancelIdleCallback?: (id: number) => void
        }
        let idleId: number | undefined
        let timeoutId: ReturnType<typeof setTimeout> | undefined

        if (typeof win.requestIdleCallback === 'function') {
            idleId = win.requestIdleCallback(() => setShouldMount(true), { timeout: 2000 })
        } else {
            timeoutId = setTimeout(() => setShouldMount(true), idleTimeout)
        }

        return () => {
            if (idleId !== undefined && typeof win.cancelIdleCallback === 'function') {
                win.cancelIdleCallback(idleId)
            }
            if (timeoutId !== undefined) clearTimeout(timeoutId)
        }
    }, [idleTimeout])

    if (!shouldMount) return null

    return (
        <video
            ref={(el) => {
                videoRef.current = el
                if (el) el.muted = true
            }}
            src={src}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            aria-hidden="true"
            className={className}
            onCanPlay={() => {
                setCanPlay(true)
                onReady?.()
                // Some browsers ignore the autoPlay attribute — nudge playback.
                videoRef.current?.play?.().catch(() => {})
            }}
            style={{
                objectFit,
                objectPosition,
                opacity: canPlay ? 1 : 0,
                transition: 'opacity 0.3s ease',
                ...style,
            }}
        />
    )
}

export default LazyLoopingVideo
